export type AttendanceSource = "APP" | "BO";

export type AttendanceMethod = "MANUAL_APP";

export type AttendanceSortField =
  | "tenant"
  | "event"
  | "person"
  | "email"
  | "host"
  | "marked_at"
  | "is_present";

export type AttendanceSortDirection = "asc" | "desc";

export interface Attendance {
  id: number;
  tenantId: string;
  tenantName: string;
  eventId: number;
  eventName: string;
  eventStartDate: string;
  personUserId: number | null;
  personName: string;
  personEmail: string | null;
  personAvatarUrl: string | null;
  personDocument: string | null;
  isCompanion: boolean;
  hostName: string | null;
  hostAvatarUrl: string | null;
  markedAt: string;
  isPresent: boolean;
  source: AttendanceSource;
  updatedAt: string;
  /** Ventana de asistencia efectiva del evento; el backend ya resuelve los defaults. */
  windowStartOffsetMin: number;
  windowEndHours: number;
}

export interface AttendanceFilters {
  tenantId: string | null;
  eventId?: number;
  /** Texto libre del filtro de evento: su nombre, o su id si son solo dígitos. */
  eventName?: string;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
  isPresent?: boolean;
  sortBy?: AttendanceSortField;
  sortDir?: AttendanceSortDirection;
}

export interface AttendanceListFilters extends AttendanceFilters {
  page: number;
  perPage: number;
}

export interface AttendancePage {
  items: Attendance[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface AttendanceHistoryPerson {
  id: number;
  name: string;
  email: string;
  avatarUrl: string | null;
}

export interface AttendanceHistoryItem {
  id: number;
  eventId: number;
  eventName: string;
  eventDate: string;
  isPresent: boolean;
  hostName: string | null;
  markedAt: string;
  /**
   * GTK-748 — el diseño muestra el avatar del host y el tipo/subtipo debajo del
   * nombre del evento. Son opcionales a propósito: si el endpoint de historial
   * todavía no los manda, la fila se renderiza igual y esos detalles no
   * aparecen, en vez de romper el mapper.
   */
  hostAvatarUrl: string | null;
  eventType: string | null;
  eventSubtype: string | null;
}

export interface AttendanceHistoryPage {
  presentCount: number | null;
  absentCount: number | null;
  person: AttendanceHistoryPerson;
  items: AttendanceHistoryItem[];
  total: number;
  page: number;
  perPage: number;
  totalPages: number;
}

export interface EventAttendanceConfig {
  id: number;
  eventId: number;
  tenantId: string;
  enabled: boolean;
  method: AttendanceMethod;
  attendanceHostUserId: number | null;
  maxAttendees: number;
  windowStartOffsetMin: number;
  windowEndHours: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface SaveEventAttendanceConfigPayload {
  eventId: number;
  enabled: boolean;
  method: AttendanceMethod;
  attendanceHostUserId: number | null;
  maxAttendees: number;
  windowStartOffsetMin: number;
  windowEndHours: number;
}

export interface AttendanceConfigDraft {
  configId: number | null;
  enabled: boolean;
  method: AttendanceMethod;
  attendanceHostUserId: number | null;
  maxAttendees: number;
  windowStartOffsetMin: number;
  windowEndHours: number;
}

/**
 * Misma regla que `isWithinAttendanceWindow` del worker: la ventana va desde
 * `start_date - window_start_offset_min` hasta `start_date + window_end_hours`.
 * Se replica acá para deshabilitar la edición con el criterio que después aplica el
 * PATCH, en vez de dejar que el admin abra el modal y se coma un 409 al guardar.
 */
export const isAttendanceWindowOpen = (
  attendance: Pick<
    Attendance,
    "eventStartDate" | "windowStartOffsetMin" | "windowEndHours"
  >,
  now: number = Date.now()
): boolean => {
  const startsAt = Date.parse(attendance.eventStartDate);
  if (Number.isNaN(startsAt)) {
    // Sin fecha de evento utilizable no hay ventana que evaluar: no bloqueamos la
    // edición, el backend sigue siendo el candado real.
    return true;
  }

  const opensAt = startsAt - attendance.windowStartOffsetMin * 60_000;
  const closesAt = startsAt + attendance.windowEndHours * 3_600_000;

  return now >= opensAt && now <= closesAt;
};

export const ATTENDANCE_EXPORT_WARNING_LIMIT = 5_000;
export const ATTENDANCE_EXPORT_HARD_LIMIT = 10_000;
export const ATTENDANCE_SUCCESS_TOAST_DURATION_MS = 4_000;

export const ATTENDANCE_DEFAULT_MAX_ATTENDEES = 20;
export const ATTENDANCE_MIN_MAX_ATTENDEES = 1;
export const ATTENDANCE_MAX_MAX_ATTENDEES = 500;
export const ATTENDANCE_DEFAULT_WINDOW_START_OFFSET_MIN = 0;
export const ATTENDANCE_DEFAULT_WINDOW_END_HOURS = 24;

export const DEFAULT_EVENT_ATTENDANCE_CONFIG: SaveEventAttendanceConfigPayload = {
  eventId: 0,
  enabled: false,
  method: "MANUAL_APP",
  attendanceHostUserId: null,
  maxAttendees: ATTENDANCE_DEFAULT_MAX_ATTENDEES,
  windowStartOffsetMin: ATTENDANCE_DEFAULT_WINDOW_START_OFFSET_MIN,
  windowEndHours: ATTENDANCE_DEFAULT_WINDOW_END_HOURS,
};

export const ATTENDANCE_MAX_WINDOW_START_OFFSET_MIN = 120;
export const ATTENDANCE_MAX_WINDOW_END_HOURS = 168;

export const getAttendanceConfigErrors = (draft: AttendanceConfigDraft | null): Partial<Record<"maxAttendees" | "windowStartOffsetMin" | "windowEndHours", string>> => {
  if (!draft || (!draft.enabled && draft.configId === null)) return {};
  const errors: Partial<Record<"maxAttendees" | "windowStartOffsetMin" | "windowEndHours", string>> = {};
  if (!Number.isInteger(draft.maxAttendees) || draft.maxAttendees < 1 || draft.maxAttendees > ATTENDANCE_MAX_MAX_ATTENDEES) {
    errors.maxAttendees = "Ingresá un número entero entre 1 y 500.";
  }
  if (!Number.isInteger(draft.windowStartOffsetMin) || draft.windowStartOffsetMin < 0 || draft.windowStartOffsetMin > ATTENDANCE_MAX_WINDOW_START_OFFSET_MIN) {
    errors.windowStartOffsetMin = "Ingresá entre 0 y 120 minutos (máximo 2 horas).";
  }
  if (!Number.isInteger(draft.windowEndHours) || draft.windowEndHours < 1 || draft.windowEndHours > ATTENDANCE_MAX_WINDOW_END_HOURS) {
    errors.windowEndHours = "Ingresá un número entero entre 1 y 168 horas.";
  }
  return errors;
};
